/**
 * Which sections of a shared evaluation a collaborator may edit. Each flag
 * stands for one tab of the compiling page and they add up freely; every
 * collaborator can *read* every tab regardless. All three off is sola lettura,
 * which is what a fresh invite gets. The owner is never a share: the backend
 * treats them as holding all three, plus the right to manage the shares.
 */
export interface TfaSharePermissions {
  /** Offerta Amministrativa (busta A): the applicant's documents. */
  administrativeOffer: boolean;
  /** Offerta Tecnica (busta B): the form answers, their attachments, the premessa, the compiler. */
  technicalOffer: boolean;
  /** Offerta Economica (busta C): the PEF, the economic offer, its attachments, the PEF report. */
  economicOffer: boolean;
}

/** Sola lettura. */
export const NO_TFA_SHARE_PERMISSIONS: Readonly<TfaSharePermissions> = {
  administrativeOffer: false,
  technicalOffer: false,
  economicOffer: false,
};

/** What the owner holds. */
export const ALL_TFA_SHARE_PERMISSIONS: Readonly<TfaSharePermissions> = {
  administrativeOffer: true,
  technicalOffer: true,
  economicOffer: true,
};

export function hasAnyTfaSharePermission(p: TfaSharePermissions): boolean {
  return p.administrativeOffer || p.technicalOffer || p.economicOffer;
}

/**
 * `Pending` until the invited email registers an account (the backend
 * activates it on sign-up), then `Active`. A revoked share keeps its row so a
 * later re-invite of the same email reuses it.
 */
export type TfaShareStatus = 'Pending' | 'Active' | 'Revoked';

export interface TfaShare {
  id: string;
  invitedEmail: string;
  permissions: TfaSharePermissions;
  status: TfaShareStatus;
  createdAt: string;
  acceptedAt: string | null;
}

export interface CreateTfaShareRequest {
  email: string;
  /** Omitted server-side means sola lettura; the client always sends it explicitly. */
  permissions: TfaSharePermissions;
}

export interface UpdateTfaShareRequest {
  permissions: TfaSharePermissions;
}

/** Who owns an evaluation that was shared with the caller. */
export interface TenderFormAnswerSharedBy {
  email: string;
  firstName: string | null;
  lastName: string | null;
}

/** Which side of a share the evaluations list shows. */
export type TenderFormAnswerOwnership = 'All' | 'Mine' | 'SharedWithMe';
